import { create } from 'zustand';
import { api } from '../lib/api-client';
import { useAuthStore } from './authStore';
import type { Asset, AssetVersion, Folder, Tag } from '@spine-workspace/shared';
import type { SpineGroup } from '../lib/spine-detector';

const BASE_URL = import.meta.env.VITE_API_URL ?? '';

interface AssetState {
  assets: Asset[];
  folders: Folder[];
  tags: Tag[];
  selectedFolderId: string | null;
  // Multi-selection
  selectedAssetIds: string[];
  selectionAnchorId: string | null;
  // Backward-compat derived value (= selectedAssetIds[0] ?? null)
  selectedAssetId: string | null;
  searchQuery: string;
  selectedTags: string[];
  loading: boolean;

  filteredAssets: () => Asset[];
  fetchAssets: () => Promise<void>;
  fetchFolders: () => Promise<void>;
  fetchTags: () => Promise<void>;
  uploadAsset: (files: FileList | File[]) => Promise<void>;
  uploadAssetGroup: (group: SpineGroup) => Promise<Asset>;
  uploadImageAsset: (file: File) => Promise<Asset>;
  deleteAsset: (id: string) => Promise<void>;
  renameAsset: (id: string, name: string) => Promise<void>;
  addTagToAsset: (assetId: string, name: string) => Promise<void>;
  removeTagFromAsset: (assetId: string, tagId: string) => Promise<void>;
  setSelectedFolder: (id: string | null) => void;
  setSearchQuery: (q: string) => void;
  toggleTag: (tagId: string) => void;

  // Selection
  selectAsset: (id: string | null, opts?: { multi?: boolean }) => void;
  selectAssets: (ids: string[]) => void;
  clearAssetSelection: () => void;

  // Folder management
  createFolder: (name: string, parentId?: string) => Promise<void>;
  renameFolder: (id: string, name: string) => Promise<void>;
  deleteFolder: (id: string) => Promise<void>;
  moveAssetToFolder: (assetId: string, folderId: string | null) => Promise<void>;
  moveAssetsToFolder: (assetIds: string[], folderId: string | null) => Promise<void>;
  moveFolder: (folderId: string, newParentId: string | null) => Promise<void>;

  // Submission workflow (per-version)
  submissions: AssetVersion[];
  submitAsset: (id: string) => Promise<void>;
  reviewAsset: (versionId: string, notes?: string, status?: 'approved' | 'rejected') => Promise<void>;
  fetchSubmissions: () => Promise<void>;

  // Version management
  versions: Record<string, AssetVersion[]>;
  fetchVersions: (assetId: string) => Promise<AssetVersion[]>;
  submitVersion: (assetId: string, versionId: string) => Promise<void>;
}

export const useAssetStore = create<AssetState>((set, get) => ({
  assets: [],
  folders: [],
  tags: [],
  selectedFolderId: null,
  selectedAssetIds: [],
  selectionAnchorId: null,
  selectedAssetId: null,
  searchQuery: '',
  selectedTags: [],
  loading: false,
  submissions: [],
  versions: {},

  filteredAssets: () => {
    const { assets, selectedFolderId, searchQuery, selectedTags } = get();
    return assets.filter((a) => {
      // Support both camelCase (API) and snake_case (raw D1) field names
      const assetFolder = a.folderId ?? (a as unknown as Record<string, unknown>)['folder_id'] as string | null ?? null;
      if (selectedFolderId !== null && assetFolder !== selectedFolderId) return false;
      if (searchQuery && !a.name.toLowerCase().includes(searchQuery.toLowerCase())) return false;
      if (selectedTags.length > 0) {
        const assetTagIds = (a.tags ?? []).map((t) => t.id);
        if (!selectedTags.every((t) => assetTagIds.includes(t))) return false;
      }
      return true;
    });
  },

  fetchAssets: async () => {
    set({ loading: true });
    try {
      const { selectedFolderId, searchQuery } = get();
      const params = new URLSearchParams();
      if (selectedFolderId !== null) params.set('folderId', selectedFolderId ?? 'null');
      if (searchQuery) params.set('search', searchQuery);
      const res = await api.get<{ assets: Asset[] }>(`/api/assets?${params}`);
      set({ assets: res.assets });
    } finally {
      set({ loading: false });
    }
  },

  fetchFolders: async () => {
    try {
      const res = await api.get<{ folders: Folder[] }>('/api/folders');
      set({ folders: res.folders });
    } catch {
      // folders endpoint added in phase 4; ignore if missing
    }
  },

  fetchTags: async () => {
    try {
      const res = await api.get<{ tags: Tag[] }>('/api/tags');
      if (Array.isArray(res.tags)) set({ tags: res.tags });
    } catch {
      // ignore
    }
  },

  addTagToAsset: async (assetId, name) => {
    const res = await api.post<{ tag: Tag }>(`/api/assets/${assetId}/tags`, { name });
    set((s) => ({
      assets: s.assets.map((a) =>
        a.id === assetId
          ? { ...a, tags: [...(a.tags ?? []).filter((t) => t.id !== res.tag.id), res.tag] }
          : a
      ),
      tags: s.tags.some((t) => t.id === res.tag.id) ? s.tags : [...s.tags, res.tag],
    }));
  },

  removeTagFromAsset: async (assetId, tagId) => {
    await api.delete(`/api/assets/${assetId}/tags/${tagId}`);
    set((s) => ({
      assets: s.assets.map((a) =>
        a.id === assetId ? { ...a, tags: (a.tags ?? []).filter((t) => t.id !== tagId) } : a
      ),
    }));
  },

  uploadAsset: async (files) => {
    const fileArray = Array.from(files);

    // PNG files: by MIME type or extension
    const pngFiles = fileArray.filter(
      (f) => f.type === 'image/png' || f.name.toLowerCase().endsWith('.png')
    );

    // Non-PNG text files: detect JSON vs atlas by content
    const textFiles = fileArray.filter(
      (f) => f.type !== 'image/png' && !f.name.toLowerCase().endsWith('.png')
    );

    let jsonFile: File | undefined;
    let atlasFile: File | undefined;

    for (const f of textFiles) {
      const text = await f.text();
      const trimmed = text.trimStart();
      if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
        // Likely JSON — validate it parses
        try {
          JSON.parse(text);
          if (!jsonFile) jsonFile = f;
          continue;
        } catch {
          // Not valid JSON, fall through to atlas
        }
      }
      // Treat as atlas (Spine atlas is a plain-text format)
      if (!atlasFile) atlasFile = f;
    }

    if (!jsonFile || !atlasFile || pngFiles.length === 0) {
      throw new Error(
        'Upload must include a Spine JSON file, an atlas file (.atlas or .txt), and at least one .png'
      );
    }

    const formData = new FormData();
    formData.append('json', jsonFile);
    formData.append('atlas', atlasFile);
    pngFiles.forEach((f, i) => formData.append(`png${i}`, f));

    const { token } = useAuthStore.getState();
    const res = await fetch(`${BASE_URL}/api/assets/upload`, {
      method: 'POST',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: formData,
    });

    if (!res.ok) {
      const err = await res.json().catch(() => ({ error: res.statusText }));
      throw new Error((err as { error: string }).error);
    }

    const { asset } = await res.json() as { asset: Asset };
    set((s) => ({ assets: [asset, ...s.assets] }));
  },

  uploadAssetGroup: async (group) => {
    // Rename skeleton to group.name.json so the API derives the correct asset name
    const skelName = group.name + '.json';
    const jsonFile = group.skeletonFile.name.toLowerCase() === 'skeleton.json'
      ? new File([group.skeletonFile], skelName, { type: group.skeletonFile.type })
      : group.skeletonFile;

    const formData = new FormData();
    formData.append('json', jsonFile);
    formData.append('atlas', group.atlasFile);
    group.pngFiles.forEach((f, i) => formData.append(`png${i}`, f));

    const { token } = useAuthStore.getState();
    const res = await fetch(`${BASE_URL}/api/assets/upload`, {
      method: 'POST',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: formData,
    });

    if (!res.ok) {
      const err = await res.json().catch(() => ({ error: res.statusText }));
      throw new Error((err as { error: string }).error);
    }

    const { asset } = await res.json() as { asset: Asset };
    set((s) => ({ assets: [asset, ...s.assets] }));
    return asset;
  },

  uploadImageAsset: async (file) => {
    const formData = new FormData();
    formData.append('image', file);
    const { token } = useAuthStore.getState();
    const res = await fetch(`${BASE_URL}/api/assets/upload/image`, {
      method: 'POST',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: formData,
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({ error: res.statusText }));
      throw new Error((err as { error: string }).error);
    }
    const { asset } = await res.json() as { asset: Asset };
    set((s) => ({ assets: [asset, ...s.assets] }));
    return asset;
  },

  deleteAsset: async (id) => {
    await api.delete(`/api/assets/${id}`);
    set((s) => {
      const nextIds = s.selectedAssetIds.filter((sid) => sid !== id);
      return {
        assets: s.assets.filter((a) => a.id !== id),
        selectedAssetIds: nextIds,
        selectedAssetId: nextIds[0] ?? null,
        selectionAnchorId: s.selectionAnchorId === id ? (nextIds[0] ?? null) : s.selectionAnchorId,
      };
    });
  },

  renameAsset: async (id, name) => {
    await api.patch(`/api/assets/${id}`, { name });
    set((s) => ({
      assets: s.assets.map((a) => (a.id === id ? { ...a, name } : a)),
    }));
  },

  setSelectedFolder: (id) => {
    set({ selectedFolderId: id });
    get().fetchAssets();
  },

  setSearchQuery: (q) => {
    set({ searchQuery: q });
  },

  selectAsset: (id, opts) => {
    if (id === null) {
      set({ selectedAssetIds: [], selectedAssetId: null, selectionAnchorId: null });
      return;
    }
    if (opts?.multi) {
      set((s) => {
        const exists = s.selectedAssetIds.includes(id);
        const nextIds = exists
          ? s.selectedAssetIds.filter((sid) => sid !== id)
          : [...s.selectedAssetIds, id];
        return {
          selectedAssetIds: nextIds,
          selectedAssetId: nextIds[0] ?? null,
          selectionAnchorId: id,
        };
      });
    } else {
      set({ selectedAssetIds: [id], selectedAssetId: id, selectionAnchorId: id });
    }
  },

  selectAssets: (ids) => {
    set((s) => ({
      selectedAssetIds: ids,
      selectedAssetId: ids[0] ?? null,
      // Keep anchor unchanged during range extension
      selectionAnchorId: s.selectionAnchorId,
    }));
  },

  clearAssetSelection: () => {
    set({ selectedAssetIds: [], selectedAssetId: null, selectionAnchorId: null });
  },

  toggleTag: (tagId) => {
    set((s) => ({
      selectedTags: s.selectedTags.includes(tagId)
        ? s.selectedTags.filter((t) => t !== tagId)
        : [...s.selectedTags, tagId],
    }));
  },

  createFolder: async (name, parentId) => {
    const res = await api.post<{ folder: Folder }>('/api/folders', { name, parentId: parentId ?? null });
    set((s) => ({ folders: [...s.folders, res.folder] }));
  },

  renameFolder: async (id, name) => {
    await api.patch(`/api/folders/${id}`, { name });
    set((s) => ({
      folders: s.folders.map((f) => (f.id === id ? { ...f, name } : f)),
    }));
  },

  deleteFolder: async (id) => {
    await api.delete(`/api/folders/${id}`);
    set((s) => ({
      folders: s.folders.filter((f) => f.id !== id),
      assets: s.assets.map((a) => (a.folderId === id ? { ...a, folderId: null } : a)),
      selectedFolderId: s.selectedFolderId === id ? null : s.selectedFolderId,
    }));
  },

  moveAssetToFolder: async (assetId, folderId) => {
    await api.patch(`/api/assets/${assetId}`, { folderId: folderId ?? null });
    set((s) => ({
      assets: s.assets.map((a) => (a.id === assetId ? { ...a, folderId: folderId } : a)),
    }));
  },

  moveAssetsToFolder: async (assetIds, folderId) => {
    await Promise.all(
      assetIds.map((id) => api.patch(`/api/assets/${id}`, { folderId: folderId ?? null }))
    );
    set((s) => ({
      assets: s.assets.map((a) =>
        assetIds.includes(a.id) ? { ...a, folderId: folderId } : a
      ),
    }));
  },

  moveFolder: async (folderId, newParentId) => {
    await api.patch(`/api/folders/${folderId}`, { parentId: newParentId ?? null });
    set((s) => ({
      folders: s.folders.map((f) =>
        f.id === folderId ? { ...f, parentId: newParentId } : f
      ),
    }));
  },

  submitAsset: async (id) => {
    await api.post(`/api/assets/${id}/submit`, {});
    set((s) => ({
      assets: s.assets.map((a) =>
        a.id === id ? { ...a, submissionStatus: 'submitted' as const } : a
      ),
    }));
  },

  reviewAsset: async (versionId, notes, status = 'approved') => {
    // versionId here is actually the asset_version.id (not asset.id)
    // We need to look up which asset this version belongs to from current submissions
    const { submissions } = useAssetStore.getState();
    const ver = (submissions as AssetVersion[]).find(v => v.id === versionId);
    const assetId = ver?.assetId ?? versionId;
    await api.post(`/api/assets/${assetId}/versions/${versionId}/review`, { status, notes });
    set((s) => ({
      submissions: (s.submissions as AssetVersion[]).filter((v) => v.id !== versionId) as AssetVersion[],
    }));
  },

  fetchSubmissions: async () => {
    try {
      const res = await api.get<{ submissions: AssetVersion[] }>('/api/assets/submissions');
      set({ submissions: res.submissions });
    } catch {
      // not admin — ignore
    }
  },

  fetchVersions: async (assetId) => {
    const res = await api.get<{ versions: AssetVersion[] }>(`/api/assets/${assetId}/versions`);
    set((s) => ({ versions: { ...s.versions, [assetId]: res.versions } }));
    return res.versions;
  },

  submitVersion: async (assetId, versionId) => {
    await api.post(`/api/assets/${assetId}/versions/${versionId}/submit`, {});
    await useAssetStore.getState().fetchVersions(assetId);
  },
}));
